import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Inbox } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../components/toast';
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Modal, PageHeader, PageLoader, Pagination, Select, Table, Td, Textarea, Th } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/format';
import { useDebouncedValue } from '../lib/hooks';
import { openSchool } from '../lib/school';
import type { IntegrationRequest, IntegrationRequestStatus, Page } from '../lib/types';

const STATUS_LABEL: Record<IntegrationRequestStatus, string> = { NEW: 'New', PLANNED: 'Planned', DECLINED: 'Not planned', DONE: 'Available' };
const STATUS_TONE = { NEW: 'amber', PLANNED: 'blue', DECLINED: 'gray', DONE: 'green' } as const;
const STATUSES = Object.keys(STATUS_LABEL) as IntegrationRequestStatus[];
const PAGE_SIZE = 25;

type RequestPage = Page<IntegrationRequest> & { counts: Partial<Record<IntegrationRequestStatus, number>> };

/** Links from a school's form are shown as links only when they're web addresses. */
const webAddress = (value: string | null) => (value && /^https?:\/\//i.test(value) ? value : null);

/** Edvora platform admins: the integrations schools have asked for. Schools can't see this list. */
export function IntegrationRequestsPage() {
  const [status, setStatus] = useState<IntegrationRequestStatus | ''>('NEW');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<IntegrationRequest | null>(null);
  const term = useDebouncedValue(search.trim(), 250);

  const query = useQuery({
    queryKey: ['platform', 'integration-requests', status, term, page],
    queryFn: () => api.get<RequestPage>('/platform/integration-requests', { status: status || undefined, search: term || undefined, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });
  const counts = query.data?.counts ?? {};
  const all = STATUSES.reduce((sum, value) => sum + (counts[value] ?? 0), 0);

  return (
    <>
      <PageHeader title="Integration requests" description="Integrations schools have asked for from the Integrations page. Only Edvora platform admins see these." />
      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4">
          <Input
            className="sm:w-72"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search by system, school, or email"
            aria-label="Search"
          />
          <Select
            className="sm:w-48"
            aria-label="Status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as IntegrationRequestStatus | '');
              setPage(1);
            }}
          >
            <option value="">Every status ({all})</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABEL[value]} ({counts[value] ?? 0})
              </option>
            ))}
          </Select>
        </div>
        {query.isLoading ? (
          <PageLoader />
        ) : query.error || !query.data ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data.items.length === 0 ? (
          <EmptyState icon={<Inbox />} title="No requests" description={status || term ? 'Nothing matches these filters.' : 'Requests schools send appear here.'} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Asked for</Th>
                  <Th>School</Th>
                  <Th>From</Th>
                  <Th>Received</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {query.data.items.map((request) => (
                  <tr key={request.id} className="cursor-pointer align-top hover:bg-slate-50" onClick={() => setOpen(request)}>
                    <Td className="max-w-md">
                      <p className="font-medium text-slate-900">{request.system}</p>
                      <p className="line-clamp-2 text-xs text-slate-500">{request.details}</p>
                    </Td>
                    <Td className="whitespace-nowrap">{request.tenant?.name ?? request.schoolName ?? '—'}</Td>
                    <Td>
                      <p className="whitespace-nowrap">{request.requesterName}</p>
                      <p className="text-xs text-slate-500">{request.requesterEmail}</p>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDate(request.createdAt)}</Td>
                    <Td>
                      <Badge tone={STATUS_TONE[request.status]}>{STATUS_LABEL[request.status]}</Badge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={query.data.total} onPage={setPage} />
          </>
        )}
      </Card>
      {open && <RequestModal request={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function RequestModal({ request, onClose }: { request: IntegrationRequest; onClose(): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState(request.status);
  const [notes, setNotes] = useState(request.notes ?? '');
  const website = webAddress(request.website);

  const save = useMutation({
    mutationFn: () => api.patch<IntegrationRequest>(`/platform/integration-requests/${request.id}`, { status, notes }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'integration-requests'] });
      toast.success('Request updated');
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={request.system}
      description={`From ${request.requesterName} at ${request.tenant?.name ?? request.schoolName ?? 'a school that’s since been removed'} · ${formatDateTime(request.createdAt)}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} disabled={status === request.status && notes === (request.notes ?? '')} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-500">What it would do</p>
          <p className="text-sm whitespace-pre-wrap text-slate-800">{request.details}</p>
        </div>
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-slate-500">Website</p>
            {website ? (
              <a href={website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 break-all text-brand-700 hover:underline">
                {website} <ExternalLink className="size-3.5 shrink-0" />
              </a>
            ) : (
              <p className="text-slate-800">{request.website ?? '—'}</p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-slate-500">Reply to</p>
            <a href={`mailto:${request.requesterEmail}?subject=${encodeURIComponent(`Your request for ${request.system}`)}`} className="break-all text-brand-700 hover:underline">
              {request.requesterEmail}
            </a>
          </div>
        </div>
        {request.tenant && (
          <Button size="sm" onClick={() => openSchool(request.tenant!.slug)}>
            Open {request.tenant.name}
          </Button>
        )}
        <div className="grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-3">
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value as IntegrationRequestStatus)}>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Notes" hint="For the Edvora team." className="sm:col-span-2">
            <Textarea rows={3} maxLength={2000} value={notes} onChange={(event) => setNotes(event.target.value)} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
